import React, { useEffect, useState } from "react";
import { Box, LinearProgress, Stack } from "@mui/joy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import { useRecoilState, useRecoilValue } from "recoil";
import useAutoFillRuns, {
  useCancelAutoFillOnLogout,
} from "hooks/useAutoFillRuns";
import useNow from "hooks/useNow";
import useOpenTask from "hooks/useOpenTask";
import { getAutoFillProgress } from "logic/autoFill";
import {
  formatClock,
  formatStepTime,
  splitStepReasoning,
} from "logic/autoFillPanel";
import {
  AutoFillRun,
  AutoFillToast,
  autoFillDockOpenAtom,
  autoFillRunsAtom,
  autoFillToastsAtom,
  autoFillViewingAtom,
  isAutoFillRunning,
} from "recoil/autoFillRunAtom";
import {
  ellipsis,
  getFieldTitle,
  getValueText,
  Spinner,
  StepRow,
  Thoughts,
} from "components/task/change-status-button/AutoFillProgress";
import { theme } from "styles/theme";

// 알림이 떠 있는 시간
const TOAST_MS = 8000;
// 최근 단계만 보여준다 (카드 높이)
const MAX_STEPS = 4;

const palette = theme.vars.palette;

const buttonReset = {
  m: 0,
  p: 0,
  border: 0,
  background: "transparent",
  color: "inherit",
  font: "inherit",
  cursor: "pointer",
  textAlign: "left",
} as const;

const pillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  maxWidth: "100%",
  minHeight: "44px",
  boxSizing: "border-box",
  borderRadius: "999px",
  border: `1px solid ${palette.Border.SuperWeak}`,
  backgroundColor: palette.Surface.Forward,
  color: palette.Content.Strong,
  boxShadow: "0 8px 24px rgba(32,39,43,0.14)",
} as const;

const text = (fontSize: number, fontWeight = 500, color?: string) => ({
  fontSize: `${fontSize}px`,
  fontWeight,
  lineHeight: 1.4,
  color: color ?? palette.Content.Strong,
});

const elapsedOf = (run: AutoFillRun, now: number) =>
  formatClock(Math.max(0, Math.floor((now - run.startedAt) / 1000)));

// 지금 단계 — 진행 중인 마지막 단계, 없으면 마지막 단계
const currentStepOf = (run: AutoFillRun) =>
  [...run.steps].reverse().find((step) => step.status === "running") ??
  run.steps[run.steps.length - 1];

const statusOf = (run: AutoFillRun) =>
  run.phase === "done"
    ? "다 채웠어요"
    : run.error || "자동 채우기를 하지 못했어요";

// 오른쪽 아래 자동 채우기 진행 표시 — 웹 전용, 화면 전체에 하나 (RootLayout).
// 접힘: 한 줄. 펼침: 지금 화면의 것(하나뿐이면 그것)은 자세히, 나머지는 한 줄씩. 위에 끝남 알림을 쌓는다
export default function AutoFillDock() {
  useCancelAutoFillOnLogout();
  const runsByKey = useRecoilValue(autoFillRunsAtom);
  const toasts = useRecoilValue(autoFillToastsAtom);
  const viewing = useRecoilValue(autoFillViewingAtom);
  const [open, setOpen] = useRecoilState(autoFillDockOpenAtom);
  const runs = Object.values(runsByKey);
  const isEmpty = runs.length === 0;
  const now = useNow(runs.some(isAutoFillRunning));

  useEffect(() => {
    if (isEmpty) setOpen(false);
  }, [isEmpty, setOpen]);

  if (isEmpty && toasts.length === 0) return null;

  // 자세히 보여 줄 실행 — 지금 화면의 것, 하나뿐이면 그것 (채우는 중일 때만)
  const detail =
    runs.find((run) => run.key === viewing && isAutoFillRunning(run)) ??
    (runs.length === 1 && isAutoFillRunning(runs[0]) ? runs[0] : undefined);

  return (
    <Stack
      sx={{
        position: "fixed",
        right: "20px",
        bottom: "calc(20px + env(safe-area-inset-bottom))",
        zIndex: 1200,
        width: "360px",
        maxWidth: "calc(100vw - 32px)",
        alignItems: "flex-end",
        gap: "8px",
        // 비어 있는 자리는 뒤 화면을 누를 수 있게
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
        "@media (max-width: 391px)": { right: "16px" },
      }}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} run={runsByKey[toast.runKey]} />
      ))}
      {!isEmpty &&
        (open ? (
          <Card
            runs={runs}
            detail={detail}
            viewing={viewing}
            now={now}
            onCollapse={() => setOpen(false)}
          />
        ) : (
          <Pill runs={runs} onExpand={() => setOpen(true)} />
        ))}
    </Stack>
  );
}

// 접힘 — 하나면 그 활동지, 여러 개면 개수
function Pill({
  runs,
  onExpand,
}: {
  runs: AutoFillRun[];
  onExpand: () => void;
}) {
  if (runs.length === 1 && !isAutoFillRunning(runs[0])) {
    return <FinishedPill run={runs[0]} />;
  }
  const run = runs[0];
  if (runs.length === 1 && run.phase === "canceling") {
    return (
      <Box sx={{ ...pillSx, px: "16px", color: palette.Content.Medium }}>
        <Spinner size="16px" color={palette.Content.Weak} />
        <Box
          component="span"
          sx={{ ...text(14, 700, palette.Content.Medium), ...ellipsis }}
        >
          {`${run.title} 중지하고 있어요`}
        </Box>
      </Box>
    );
  }
  const runningCount = runs.filter(isAutoFillRunning).length;
  const progress = getAutoFillProgress(run.request?.fields || [], run.values);
  const step = currentStepOf(run);
  return (
    <Box
      component="button"
      type="button"
      aria-expanded={false}
      onClick={onExpand}
      sx={{ ...buttonReset, ...pillSx, pl: "16px", pr: "10px" }}
    >
      {runningCount > 0 ? (
        <Spinner size="16px" />
      ) : (
        <CheckRoundedIcon
          sx={{ fontSize: "16px", color: palette.Content.Highlight }}
        />
      )}
      {runs.length === 1 ? (
        <>
          <Box component="span" sx={{ ...text(14, 700), whiteSpace: "nowrap" }}>
            {run.title}
          </Box>
          <Box
            component="span"
            sx={{
              ...text(13, 500, palette.Content.Medium),
              ...ellipsis,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {[
              step?.label,
              progress.total > 0 && `${progress.written}/${progress.total}칸`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Box>
        </>
      ) : (
        <Box component="span" sx={{ ...text(14, 700), whiteSpace: "nowrap" }}>
          {runningCount > 0
            ? `자동 채우는 중 ${runningCount}개`
            : `자동 채우기 ${runs.length}개 끝났어요`}
        </Box>
      )}
      <KeyboardArrowUpRoundedIcon
        sx={{ flexShrink: 0, fontSize: "18px", color: palette.Content.Medium }}
      />
    </Box>
  );
}

// 끝났는데 아직 안 연 것 — 열거나 닫을 때까지 남는다
function FinishedPill({ run }: { run: AutoFillRun }) {
  const openTask = useOpenTask({
    taskKey: run.taskKey,
    thoughtRecordKey: run.thoughtRecordKey,
  });
  const { remove } = useAutoFillRuns();
  return (
    <Box
      sx={{
        ...pillSx,
        pl: "16px",
        pr: "4px",
        borderColor: palette.Content.SoftHighlight,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={openTask}
        sx={{
          ...buttonReset,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minWidth: 0,
          minHeight: "44px",
        }}
      >
        <StatusIcon run={run} />
        <Box component="span" sx={{ ...text(14, 700), whiteSpace: "nowrap" }}>
          {run.title}
        </Box>
        <Box
          component="span"
          sx={{ ...text(13, 500, palette.Content.Medium), ...ellipsis }}
        >
          {run.phase === "done" ? "다 채웠어요" : "채우지 못했어요"}
        </Box>
        <Box
          component="span"
          sx={{ ...text(14, 700, palette.Content.Highlight), flexShrink: 0 }}
        >
          열기
        </Box>
      </Box>
      <CloseButton onClick={() => remove(run.key)} />
    </Box>
  );
}

// 펼침
function Card({
  runs,
  detail,
  viewing,
  now,
  onCollapse,
}: {
  runs: AutoFillRun[];
  detail?: AutoFillRun;
  viewing?: string;
  now: number;
  onCollapse: () => void;
}) {
  const others = runs.filter((run) => run !== detail);
  const runningCount = runs.filter(isAutoFillRunning).length;
  const cardSx = {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "20px",
    border: `1px solid ${palette.Border.SuperWeak}`,
    backgroundColor: palette.Surface.Forward,
    color: palette.Content.Strong,
    boxShadow: "0 16px 40px rgba(32,39,43,0.16), 0 2px 6px rgba(32,39,43,0.06)",
    overflow: "hidden",
    maxHeight: "calc(100vh - 120px)",
  } as const;

  // 하나뿐이고 채우는 중 — 그 활동지 카드
  if (detail && others.length === 0) {
    const isStopping = detail.phase === "canceling";
    return (
      <Stack
        component="section"
        aria-label="자동 채우기 진행"
        sx={{ ...cardSx, p: "16px", gap: "14px", overflowY: "auto" }}
      >
        <Stack direction="row" alignItems="center" sx={{ gap: "8px" }}>
          <Spinner
            size="18px"
            color={isStopping ? palette.Content.Weak : undefined}
          />
          {/* 지금 화면이 아니면 제목으로 그 활동지에 간다 */}
          {viewing === detail.key ? (
            <Box
              component="span"
              sx={{ ...text(15, 700), flex: 1, minWidth: 0 }}
            >
              {isStopping
                ? `${detail.title} 중지하고 있어요`
                : `${detail.title} 채우는 중`}
            </Box>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ flex: 1, minWidth: 0, gap: "4px" }}
            >
              <TitleLink run={detail} size={15} />
              <Box component="span" sx={{ ...text(15, 700), flexShrink: 0 }}>
                {isStopping ? "중지하고 있어요" : "채우는 중"}
              </Box>
            </Stack>
          )}
          <Clock>{elapsedOf(detail, now)}</Clock>
          <CollapseButton onClick={onCollapse} />
        </Stack>
        <RunDetail run={detail} now={now} />
        <Stack direction="row" justifyContent="flex-end">
          <StopButton run={detail} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack component="section" aria-label="자동 채우기 진행" sx={cardSx}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          flexShrink: 0,
          gap: "8px",
          p: "14px 12px 12px 16px",
          borderBottom: `1px solid ${palette.Border.SuperWeak}`,
        }}
      >
        <Box component="span" sx={{ ...text(15, 700), flex: 1 }}>
          {runningCount > 0
            ? `자동 채우는 중 ${runningCount}개`
            : `자동 채우기 ${runs.length}개 끝났어요`}
        </Box>
        <CollapseButton onClick={onCollapse} />
      </Stack>
      <Box sx={{ minHeight: 0, overflowY: "auto" }}>
        {detail && (
          <Stack sx={{ p: "14px 16px 16px", gap: "12px" }}>
            <Stack direction="row" alignItems="center" sx={{ gap: "8px" }}>
              <Spinner
                size="16px"
                color={
                  detail.phase === "canceling"
                    ? palette.Content.Weak
                    : undefined
                }
              />
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                {viewing === detail.key ? (
                  <Box component="span" sx={{ ...text(14, 700), ...ellipsis }}>
                    {detail.title}
                  </Box>
                ) : (
                  <TitleLink run={detail} />
                )}
              </Box>
              {viewing === detail.key && (
                <Box
                  component="span"
                  sx={{
                    ...text(12, 700, palette.Content.Highlight),
                    flexShrink: 0,
                    p: "3px 8px",
                    borderRadius: "999px",
                    backgroundColor: palette.Surface.SoftHighlight,
                  }}
                >
                  지금 화면
                </Box>
              )}
              <Clock>{elapsedOf(detail, now)}</Clock>
            </Stack>
            <RunDetail run={detail} now={now} />
            <Stack direction="row" justifyContent="flex-end">
              <StopButton run={detail} />
            </Stack>
          </Stack>
        )}
        {others.map((run, index) => (
          <RunRow
            key={run.key}
            run={run}
            now={now}
            divided={!!detail || index > 0}
          />
        ))}
      </Box>
    </Stack>
  );
}

// 자세히 — 진행 팝업(AutoFillProgress)의 진행률·단계·방금 쓴 칸
function RunDetail({ run, now }: { run: AutoFillRun; now: number }) {
  const fields = run.request?.fields || [];
  const progress = getAutoFillProgress(fields, run.values);
  const reasoning = run.steps.map(splitStepReasoning);
  const firstVisibleStep = Math.max(0, run.steps.length - MAX_STEPS);
  // 펼친 모델 생각 — 단계 순서(steps index)
  const [openThoughts, setOpenThoughts] = useState<number[]>([]);
  const latestField = run.currentKey
    ? fields.find((field) => field.key === run.currentKey)
    : undefined;
  const latestValue = run.currentKey ? run.values[run.currentKey] : undefined;

  return (
    <>
      {/* 진행률 — 지금 보이는 필수 칸 기준. 첫 값이 오기 전에는 진행률 미정 */}
      <Stack sx={{ gap: "6px" }}>
        <LinearProgress
          {...(progress.written > 0
            ? {
                determinate: true,
                value:
                  progress.total > 0
                    ? (progress.written / progress.total) * 100
                    : 0,
              }
            : {})}
          variant="plain"
          sx={{
            "--LinearProgress-thickness": "8px",
            "--LinearProgress-radius": "100px",
            "--LinearProgress-progressRadius": "100px",
            backgroundColor: palette.Surface.Backward,
            color: palette.Surface.Highlight,
          }}
        />
        {progress.total > 0 && (
          <Box
            component="span"
            sx={{
              ...text(12, 500, palette.Content.Medium),
              alignSelf: "flex-end",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {`${progress.written} / ${progress.total}칸`}
          </Box>
        )}
      </Stack>

      {/* 단계 */}
      <Stack
        component="ol"
        sx={{
          m: 0,
          p: "12px 14px",
          listStyle: "none",
          gap: "8px",
          borderRadius: "12px",
          backgroundColor: palette.Background.Level1,
        }}
      >
        {run.steps.slice(firstVisibleStep).map((step, i) => {
          const index = firstVisibleStep + i;
          const { thoughts } = reasoning[index];
          return (
            <Stack
              component="li"
              key={`${step.name}_${index}`}
              sx={{ gap: "4px" }}
            >
              <StepRow
                running={step.status === "running"}
                time={formatStepTime(step, now)}
              >
                {step.label}
              </StepRow>
              {/* 단계 결과 요약 (지난 기록 수·읽은 문서·다시 쓰는 칸 수) */}
              {step.summary && (
                <Box
                  component="span"
                  sx={{ ...text(12, 500, palette.Content.Medium), pl: "24px" }}
                >
                  {step.summary}
                </Box>
              )}
              {step.tools.map((tool) => (
                <Box key={tool.id} sx={{ pl: "24px" }}>
                  <StepRow running={tool.status === "running"} small>
                    {tool.label}
                  </StepRow>
                </Box>
              ))}
              {thoughts.length > 0 && (
                <Thoughts
                  text={thoughts.join("\n\n")}
                  open={openThoughts.includes(index)}
                  onToggle={() =>
                    setOpenThoughts((prev) =>
                      prev.includes(index)
                        ? prev.filter((each) => each !== index)
                        : [...prev, index]
                    )
                  }
                />
              )}
            </Stack>
          );
        })}
      </Stack>

      {/* 방금 쓴 칸 */}
      {latestField && latestValue !== undefined && (
        <Stack sx={{ gap: "4px", px: "4px" }}>
          <Box component="span" sx={text(12, 500, palette.Content.Medium)}>
            {`방금 쓴 칸 · ${getFieldTitle(latestField) ?? ""}`}
          </Box>
          <Box
            component="span"
            sx={{
              ...text(14),
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {getValueText(latestField, latestValue)}
          </Box>
        </Stack>
      )}
    </>
  );
}

// 펼침의 나머지 활동지 — 한 줄씩
function RunRow({
  run,
  now,
  divided,
}: {
  run: AutoFillRun;
  now: number;
  divided: boolean;
}) {
  const { remove } = useAutoFillRuns();
  // 채우는 중이면 자세히 펼쳐 볼 수 있다 (제목은 그 활동지로 이동)
  const [isExpanded, setIsExpanded] = useState(false);
  const isRunning = isAutoFillRunning(run);
  const status =
    run.phase === "running"
      ? `${currentStepOf(run)?.label ?? ""} · ${elapsedOf(run, now)}`
      : run.phase === "canceling"
        ? "중지하고 있어요"
        : statusOf(run);
  return (
    <Box
      sx={{ borderTop: divided ? `1px solid ${palette.Border.SuperWeak}` : 0 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ gap: "10px", p: "12px 12px 12px 16px" }}
      >
        <StatusIcon run={run} />
        <Stack sx={{ flex: 1, minWidth: 0, gap: "2px" }}>
          <TitleLink run={run} />
          <Box
            component="span"
            sx={{
              ...text(12, 500, palette.Content.Medium),
              ...ellipsis,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {status}
          </Box>
        </Stack>
        {isRunning ? (
          <ToggleButton
            expanded={isExpanded}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        ) : (
          <TextButton muted onClick={() => remove(run.key)}>
            닫기
          </TextButton>
        )}
      </Stack>
      {isRunning && isExpanded && (
        <Stack sx={{ p: "0 16px 16px", gap: "12px" }}>
          <RunDetail run={run} now={now} />
          <Stack direction="row" justifyContent="flex-end">
            <StopButton run={run} />
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

// 끝남 알림 — 8초 뒤 사라진다
function Toast({ toast, run }: { toast: AutoFillToast; run?: AutoFillRun }) {
  const { closeToast } = useAutoFillRuns();
  const openTask = useOpenTask({
    taskKey: run?.taskKey ?? "",
    thoughtRecordKey: run?.thoughtRecordKey,
  });

  useEffect(() => {
    const timer = setTimeout(() => closeToast(toast.id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast.id, closeToast]);

  return (
    <Stack
      role={toast.kind === "error" ? "alert" : "status"}
      direction="row"
      alignItems="center"
      sx={{
        width: "100%",
        boxSizing: "border-box",
        gap: "4px",
        p: "12px 6px 12px 16px",
        borderRadius: "14px",
        backgroundColor: palette.Content.Strong,
        color: palette.Content.Negative,
        boxShadow: "0 12px 32px rgba(32,39,43,0.24)",
      }}
    >
      {toast.kind === "error" && (
        <ErrorOutlineRoundedIcon
          sx={{
            flexShrink: 0,
            mr: "6px",
            fontSize: "18px",
            color: palette.Content.HighlightWithAlert,
          }}
        />
      )}
      <Stack sx={{ flex: 1, minWidth: 0, gap: "2px" }}>
        <Box
          component="span"
          sx={{ ...text(12, 500, "rgba(255,255,255,0.72)"), ...ellipsis }}
        >
          {toast.title}
        </Box>
        <Box component="span" sx={text(14, 700, palette.Content.Negative)}>
          {toast.message}
        </Box>
      </Stack>
      {toast.kind !== "filled" && run && (
        <TextButton inverse onClick={openTask}>
          열기
        </TextButton>
      )}
      <CloseButton inverse onClick={() => closeToast(toast.id)} />
    </Stack>
  );
}

function StatusIcon({ run }: { run: AutoFillRun }) {
  if (isAutoFillRunning(run)) {
    return (
      <Spinner
        size="16px"
        color={run.phase === "canceling" ? palette.Content.Weak : undefined}
      />
    );
  }
  return run.phase === "done" ? (
    <CheckRoundedIcon
      sx={{ flexShrink: 0, fontSize: "16px", color: palette.Content.Highlight }}
    />
  ) : (
    <ErrorOutlineRoundedIcon
      sx={{
        flexShrink: 0,
        fontSize: "16px",
        color: palette.Content.HighlightWithAlert,
      }}
    />
  );
}

function Clock({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        ...text(13, 500, palette.Content.Medium),
        flexShrink: 0,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {children}
    </Box>
  );
}

function StopButton({ run }: { run: AutoFillRun }) {
  const { cancel } = useAutoFillRuns();
  const isStopping = run.phase === "canceling";
  return (
    <Box
      component="button"
      type="button"
      disabled={isStopping}
      onClick={() => cancel(run.key)}
      sx={{
        ...buttonReset,
        ...text(14, 700, isStopping ? palette.Content.SuperWeak : undefined),
        flexShrink: 0,
        minHeight: "36px",
        px: "14px",
        borderRadius: "999px",
        backgroundColor: palette.Background.Level1,
        cursor: isStopping ? "default" : "pointer",
      }}
    >
      중지
    </Box>
  );
}

// 열기·닫기 — 글자 버튼. inverse: 어두운 알림 위
function TextButton({
  onClick,
  muted,
  inverse,
  children,
}: {
  onClick: () => void;
  muted?: boolean;
  inverse?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        ...buttonReset,
        ...text(
          14,
          700,
          inverse
            ? palette.Content.Negative
            : muted
              ? palette.Content.Medium
              : palette.Content.Highlight
        ),
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        minHeight: "36px",
        px: "12px",
        borderRadius: "999px",
      }}
    >
      {children}
    </Box>
  );
}

function CollapseButton({ onClick }: { onClick: () => void }) {
  return <ToggleButton expanded onClick={onClick} label="접기" />;
}

// 펼치고 접는 화살표 — 카드 접기(label)는 늘 아래, 줄 자세히는 펼치면 위로 돈다
function ToggleButton({
  expanded,
  onClick,
  label,
}: {
  expanded: boolean;
  onClick: () => void;
  label?: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label ?? (expanded ? "자세히 접기" : "자세히 보기")}
      aria-expanded={expanded}
      onClick={onClick}
      sx={{
        ...buttonReset,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "36px",
        height: "36px",
        borderRadius: "999px",
        color: palette.Content.Medium,
      }}
    >
      <KeyboardArrowDownRoundedIcon
        sx={{
          fontSize: "20px",
          transform: label || !expanded ? "none" : "rotate(180deg)",
          transition: "transform 0.2s",
        }}
      />
    </Box>
  );
}

function CloseButton({
  onClick,
  inverse,
}: {
  onClick: () => void;
  inverse?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label="닫기"
      onClick={onClick}
      sx={{
        ...buttonReset,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: inverse ? "44px" : "36px",
        height: inverse ? "44px" : "36px",
        borderRadius: "999px",
        color: inverse ? "rgba(255,255,255,0.8)" : palette.Content.Medium,
      }}
    >
      <CloseRoundedIcon sx={{ fontSize: "16px" }} />
    </Box>
  );
}

// 활동지 제목 — 누르면 그 활동지로 간다 (↗, TaskLink 와 같은 표시)
function TitleLink({ run, size = 14 }: { run: AutoFillRun; size?: number }) {
  const openTask = useOpenTask({
    taskKey: run.taskKey,
    thoughtRecordKey: run.thoughtRecordKey,
  });
  return (
    <Box
      component="button"
      type="button"
      onClick={openTask}
      sx={{
        ...buttonReset,
        ...text(size, 700),
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        maxWidth: "100%",
        minWidth: 0,
        "&:hover > span": { textDecoration: "underline" },
      }}
    >
      <Box component="span" sx={ellipsis}>
        {run.title}
      </Box>
      <NorthEastRoundedIcon
        aria-hidden
        sx={{ flexShrink: 0, fontSize: "14px", color: palette.Content.Weak }}
      />
    </Box>
  );
}
